import React from 'react';

import { makeUclComponent } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';
import { withTypography } from 'ucl/components/text/Typography';

import { ExternalLink } from 'components/external-link';
import { LayoutContainer } from 'components/layout/styled';
import PictureComponent from 'components/picture';
import { Box, BoxProps } from 'components/ui/box';
import { useIsDesktopLargeBreakpoint } from 'hooks/use-media-query';
import { getDesignToken, spacing } from 'styles/constants';

export const LayoutContainerBackground = LayoutContainer.withConfig({
  backgroundColor: getDesignToken('background-focus'),
  marginTop: '150px',
  paddingBottom: {
    base: '$6',
    md: '$0',
  },
});

export const Section = ({ style, ...props }: BoxProps) => {
  const isDesktop = useIsDesktopLargeBreakpoint();
  return React.createElement(Box, {
    ...props,
    style: [styles.section, isDesktop && styles.sectionDesktop, style],
  });
};

export const ImageDesktop = ({ style, ...props }: BoxProps) => {
  const isDesktop = useIsDesktopLargeBreakpoint();
  return React.createElement(Box, {
    ...props,
    style: [styles.imageDesktop, isDesktop && styles.imageDesktopDesktop, style],
  });
};

export const TextHalf = ({ style, ...props }: BoxProps) => {
  const isDesktop = useIsDesktopLargeBreakpoint();
  return React.createElement(Box, {
    ...props,
    style: [styles.textHalf, isDesktop && styles.textHalfDesktop, style],
  });
};

export const ImageMobile = ({ style, ...props }: BoxProps) => {
  const isDesktop = useIsDesktopLargeBreakpoint();
  return React.createElement(Box, {
    ...props,
    style: [styles.imageMobile, isDesktop && styles.imageMobileDesktop, style],
  });
};

export const MainImageContainer = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.mainImageContainer, style] });

export const Picture = PictureComponent.withConfig({
  width: '100%',
  height: '110%',
});

export const PictureMobile = Picture.withConfig({
  position: 'absolute',
  height: '$56',
  bottom: '$0',
});

export const Header = withTypography({
  fontFamily: 'heading',
  bold: true,
  fontSize: '3xl',
  lineHeight: 34,
});

export const Description = withTypography({ mt: '$8' });

export const BadgesContainer = ({ style, ...props }: BoxProps) => {
  const isDesktop = useIsDesktopLargeBreakpoint();
  return React.createElement(Box, {
    ...props,
    style: [styles.badgesContainer, isDesktop && styles.badgesDesktop, style],
  });
};

export const BadgeLink = makeUclComponent(ExternalLink).withConfig({
  marginRight: {
    base: '$0',
    md: '$2',
    lg: '$6',
  },
});

export const Disclaimer = withTypography({
  fontSize: 'sm',
  textAlign: 'center',
  mt: '$4',
});

export const HeaderContainer = ({ style, ...props }: BoxProps) => {
  const isDesktop = useIsDesktopLargeBreakpoint();
  return React.createElement(Box, {
    ...props,
    style: [styles.headerContainer, isDesktop && styles.headerDesktop, style],
  });
};

const styles = StyleSheet.create({
  section: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
    position: 'relative',
    width: '100%',
  },
  sectionDesktop: {
    alignItems: 'stretch',
    flexDirection: 'row',
    padding: 0,
  },
  imageDesktop: {
    display: 'none',
  },
  imageDesktopDesktop: {
    display: 'flex',
    flex: 1,
  },
  textHalf: {
    alignItems: 'center',
    flex: 1,
    width: '100%',
  },
  textHalfDesktop: {
    alignItems: 'flex-start',
    paddingBottom: spacing.$20,
    paddingTop: spacing.$12,
  },
  imageMobile: {
    bottom: 0,
    flex: 1,
    height: spacing.$48,
    maxWidth: spacing.$40,
    right: 0,
  },
  imageMobileDesktop: {
    display: 'none',
  },
  mainImageContainer: {
    bottom: '10%',
    height: '100%',
    paddingBottom: spacing.$10,
    paddingHorizontal: '10%',
  },
  badgesContainer: {
    flexDirection: 'row',
    gap: spacing.$5,
    marginTop: spacing.$4,
  },
  badgesDesktop: {
    gap: 0,
    marginTop: spacing.$8,
  },
  headerContainer: {
    flex: 1,
    justifyContent: 'center',
    paddingLeft: spacing.$4,
  },
  headerDesktop: {
    paddingLeft: 0,
  },
});
